import {
	ElementType,
	PayloadList,
	ElementSchema,
	logicType,
	businessLogicValue,
	SortValue,
	FilterProperty,
} from "schemas/elements";
import {
	businessLogicPropertyType,
	FormBusinessLogicUpdateDataValidation,
	ElementValidation,
	formBusinessLogicPropertyValidation,
} from "hooks/FormDesigner/useElementList";
import _ from "lodash";
import elementsSchema from "schemas";
import { filterValues } from "../constants";

type Props = {
	item: ElementType;
	index: number;
	updateElementValidation: (data: ElementValidation) => void;
};

export default ({ item, index, updateElementValidation }: Props) => {
	let fieldsMissing = false;
	item.jsonRequired.some(property => {
		switch (property) {
			case "name":
			case "label":
			case "hint":
			case "value":
			case "looksAt":
			case "labelField":
				if (!TextValidation(item[property])) {
					fieldsMissing = true;
					return true;
				}
				break;

			case "id":
				if (typeof item.id === "string" && !TextValidation(item.id)) {
					fieldsMissing = true;
					return true;
				} else if (
					typeof item.id !== "string" &&
					!TextValidation(item.id?.value)
				) {
					fieldsMissing = true;
					return true;
				}
				break;

			case "optionsId":
			case "optionsPath":
			case "searchField":
			case "resource":
			case "link":
				if (!TextValidation(item[property]?.value)) {
					fieldsMissing = true;
					return true;
				}
				break;

			case "numberFormat":
				if (
					item[property]?.precision !== undefined ||
					(item[property]?.rounding !== undefined &&
						item[property]?.rounding !== null)
				) {
					if (!TextValidation(item[property]?.rounding?.value)) {
						fieldsMissing = true;
						return true;
					}
					if (!NumberValidation(item[property]?.precision)) {
						fieldsMissing = true;
						return true;
					}
				}
				break;

			case "elements":
				if (!NestedValidation(item.elements)) {
					fieldsMissing = true;
					return true;
				}
				break;
			case "search":
				if (
					(item.elem === "legacyArrayView" &&
						item.searchField !== undefined &&
						item.searchField !== null) ||
					item.elem !== "legacyArrayView"
				) {
					if (
						item.elem !== "legacyArrayView" &&
						!TextValidation(item.search?.label?.value)
					) {
						fieldsMissing = true;
						return true;
					}
					if (
						item.elem === "reference" ||
						item.elem === "legacyArrayView"
					) {
						if (
							item.search?.columns !== undefined &&
							item.search?.columns.length > 0
						) {
							item.search?.columns.forEach(column => {
								if (
									!TextValidation(column.id?.value) ||
									!TextValidation(column.label)
								) {
									fieldsMissing = true;
									return true;
								} else {
									return false;
								}
							});
						} else {
							fieldsMissing = true;
							return true;
						}
					}
					if (item.search?.view && item.search?.view.length > 0) {
						if (!NestedValidation(item.search.view)) {
							fieldsMissing = true;
							return true;
						}
					}
					const myElementsSchema: ElementSchema =
						elementsSchema as ElementSchema;
					Object.keys(
						myElementsSchema[item.elem].search.rules.payload.rules
					).map(property2 => {
						const thePayloadProperty = property2 as PayloadList;

						if (
							(thePayloadProperty !== "sort" &&
								thePayloadProperty !== "filter" &&
								!TextValidation(
									item.search?.payload[
										thePayloadProperty
									]?.toString()
								)) ||
							(thePayloadProperty === "sort" &&
								!SortValidation(
									item.search?.payload[
										thePayloadProperty
									] as SortValue[]
								)) ||
							(thePayloadProperty === "filter" &&
								!FilterValidation(
									item.search?.payload[thePayloadProperty]
								))
						) {
							fieldsMissing = true;
							return true;
						}
						return false;
					});
				}
				break;
		}
		return false;
	});

	if (item.afterPatch) {
		if (!isBusinessLogicValid(item.afterPatch)) fieldsMissing = true;
	}

	if (fieldsMissing && item.formValidation === false) {
		updateElementValidation({
			validation: true,
			elementIndex: index,
		});
	} else if (!fieldsMissing && item.formValidation === true) {
		updateElementValidation({
			validation: false,
			elementIndex: index,
		});
	}
};

export const runFormBusinessLogicValidation = ({
	value,
	action,
	updateFormBusinessLogicValidation,
}: {
	value: businessLogicValue;
	action: businessLogicPropertyType;
	updateFormBusinessLogicValidation: (
		data: FormBusinessLogicUpdateDataValidation
	) => void;
}) => {
	let fieldsMissing = false;
	if (!isBusinessLogicValid(value)) fieldsMissing = true;
	if (fieldsMissing) {
		updateFormBusinessLogicValidation({
			validation: true,
			property:
				`${action}Validation` as formBusinessLogicPropertyValidation,
		});
	} else {
		updateFormBusinessLogicValidation({
			validation: false,
			property:
				`${action}Validation` as formBusinessLogicPropertyValidation,
		});
	}
};

export const isBusinessLogicValid = (value: businessLogicValue): boolean => {
	let valid = true;
	const { logic, dataMap } = value;
	if (logic.length !== 0 || dataMap.length !== 0) {
		if (logic?.length === 0) valid = false;
		logic?.forEach(({ name, params }: logicType) => {
			if (!TextValidation(name)) valid = false;
			if (!ParamsValidation(params)) valid = false;
		});
		if (!DataMapValidation(dataMap)) valid = false;
	}
	return valid;
};

export const DataMapValidation = (value?: string[][]): boolean => {
	let valid = true;
	value?.forEach((data: string[]) => {
		if (!TextValidation(data[0])) {
			valid = false;
		}
	});
	return valid;
};

export const ParamsValidation = (value: { [key: string]: string }): boolean => {
	let valid = true;
	Object.entries(value).forEach(param => {
		if (!TextValidation(param[0]) || !TextValidation(param[1]))
			valid = false;
	});
	return valid;
};

export const SortValidation = (values: SortValue[]): boolean => {
	let isValid = true;
	values?.forEach((item: SortValue) => {
		if (!isValid) return;
		Object.values(item).forEach(prop => {
			if (!TextValidation(prop)) isValid = false;
			return;
		});
	});
	return isValid;
};

export const FilterValidation = (
	values: FilterProperty[],
	nested?: boolean
): boolean => {
	let isValid = true;
	values?.forEach(({ field, value, filter }: FilterProperty) => {
		if (!isValid) return;
		if (!TextValidation(field) && !nested) isValid = false;
		if (filter === filterValues.OR) {
			const nestedValid = FilterValidation(value, true);
			if (!nestedValid) isValid = false;
		} else {
			if (!TextValidation(value.value)) isValid = false;
		}
	});

	return isValid;
};

export const TextValidation = (value: string | undefined): boolean => {
	if (value !== undefined && value.toString().trim() !== "") {
		return true;
	} else {
		return false;
	}
};

export const NumberValidation = (value: number | undefined): boolean => {
	if (value !== undefined && !isNaN(value)) {
		return true;
	} else {
		return false;
	}
};

export const RegexValidation = (value: string | undefined): boolean => {
	if (value !== undefined) {
		try {
			new RegExp(value);
		} catch {
			return false;
		}
		return true;
	} else {
		// This returns true if it is undefined as the only regex isn't in a required property
		return true;
	}
};

export const StringArrayValidation = (
	value: string[] | string | undefined
): boolean => {
	if (value !== undefined && Array.isArray(value) && value.length > 0) {
		return true;
	} else {
		return false;
	}
};

export const JsonValidation = (
	value: string | undefined,
	type: "array" | "object"
): boolean => {
	if (value !== undefined) {
		try {
			const parsed = JSON.parse(value);
			if (_.isEmpty(parsed)) return false;
			if (type === "array" && parsed && Array.isArray(parsed)) {
				let fieldsMissing = false;
				parsed.forEach(item => {
					if (!JsonValidation(JSON.stringify(item), "object")) {
						fieldsMissing = true;
						return false;
					}
				});
				if (fieldsMissing) {
					return false;
				} else {
					return true;
				}
			} else if (
				type === "object" &&
				parsed &&
				typeof parsed === "object" &&
				!Array.isArray(parsed)
			) {
				return true;
			} else {
				return false;
			}
		} catch {
			return false;
		}
	} else {
		return false;
	}
};

export const NestedValidation = (elements?: ElementType[]): boolean => {
	if (elements === undefined || elements.length < 1) {
		return false;
	} else {
		let fieldsMissing = false;
		elements.some(element => {
			const { formValidation } = element;

			if (formValidation) {
				fieldsMissing = true;
				return true;
			}
			return false;
		});
		if (fieldsMissing) {
			return false;
		} else {
			return true;
		}
	}
};
